import React, { useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { isCasinoGamePage } from 'utils';

import { Layout } from '@atlas/components';
import { PreferencesProvider, SiteConfigManager } from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import { ScrollableViewportObserver } from '@atlas/ui';

import { BannerAddToHomeScreenIOS } from 'common/BannerAddToHomeScreenIOS';
import { BannerApplicationLinkAndroid } from 'common/BannerApplicationLinkAndroid';
import BetslipButtonNewNav from 'common/Betslip/BetslipButtonNewNav';
import { LocalBetslipContextProvider } from 'common/Betslip/LocalBetslipContext';
import GTMInitializer from 'common/GTMInitializer';
import CookiesPopup from 'common/Popup/CookiesPopup';
import { DemoPopup } from 'common/Popup/DemoPopup';
import MultiPopupManager from 'common/Popup/MultiPopup/MultiPopupManager';
import { OneButtonCookiesPopup } from 'common/Popup/OneButtonCookies';
import { TerminalLoginPopupManager } from 'common/Popup/TerminalLoginPopup/TerminalLoginPopupManager';
import SimplifiedSportsbookViewProvider from 'common/SimplifiedSportsbook/SimplifiedSportsbookViewProvider';
import QueryRoutes from 'components/Layout/QueryRoutes';
import NavigationBar from 'components/NavigationBar/NavigationBar';
import NewNavigationBar from 'components/NavigationBar/NewNavigationBar';
import { useShowPopupSignUpDelay } from 'hooks/useShowPopupSignUpDelay';

import Betslip from '../../common/Betslip';
import ExtendedFlyingBetslip from '../../common/Betslip/ExtendedFlyingBetslip';
import Header from '../Header/Header';
import SimpleNavigation from '../SimpleNavigation/SimpleNavigation';

const DefaultLayout = props => {
  const {
    AuthorizationLoader,
    betslipNavConfig,
    children,
    isSimplifiedView,
    layoutClassName,
    localBetslipState,
    wrapperClassName,
  } = props;
  const {
    location: { pathname },
  } = useHistory();
  const cookiesType = SiteConfigManager.getConfig('config.config.general.cookiesType', false);
  const enableCookiesPopup = SiteConfigManager.getConfig(
    'config.config.general.enableCookiesPopup',
    true,
  );
  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
  const casinoLivePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO_LIVE);

  const isNotCasinoGamePage = useMemo(
    () => !isCasinoGamePage(pathname, [casinoPath, casinoLivePath]),
    [casinoLivePath, casinoPath, pathname],
  );
  const showDemoPopup = isSimplifiedView || isNotCasinoGamePage;

  useShowPopupSignUpDelay();

  const hideForAltenar = () => {
    const isAltenar = SiteConfigManager.getConfig(
      'config.config.sportsbookIntegrator.altenarIntegrationName',
    );
    return (
      isAltenar && (pathname === '/sportsbook' || pathname === '/live' || pathname === '/results')
    );
  };
  return (
    <>
      <BannerAddToHomeScreenIOS />
      <BannerApplicationLinkAndroid />

      <LocalBetslipContextProvider value={localBetslipState}>
        <Layout>
          <Layout.Header sticky>
            <Header />
          </Layout.Header>
          <Layout.Content>
            <PreferencesProvider>
              <SimplifiedSportsbookViewProvider>
                <ScrollableViewportObserver className={wrapperClassName} id="content">
                  <div className={layoutClassName}>
                    {isSimplifiedView && <SimpleNavigation />}
                    <AuthorizationLoader>{children}</AuthorizationLoader>
                    {betslipNavConfig.hasExtended && <ExtendedFlyingBetslip />}
                  </div>
                </ScrollableViewportObserver>
              </SimplifiedSportsbookViewProvider>
            </PreferencesProvider>
          </Layout.Content>
          {hideForAltenar() ? null : (
            <Layout.BottomMenu>
              {betslipNavConfig.hasNewNavigation ? (
                <>
                  <NewNavigationBar />
                  {betslipNavConfig.hasSimple && <BetslipButtonNewNav />}
                </>
              ) : (
                <NavigationBar />
              )}
            </Layout.BottomMenu>
          )}
        </Layout>

        <Betslip />
      </LocalBetslipContextProvider>

      <QueryRoutes />
      <Link className="page-list-link" to="/pages">
        Pages
      </Link>
      {enableCookiesPopup && cookiesType === 'Decline+Accept' && <CookiesPopup />}
      {enableCookiesPopup && cookiesType === 'Ok' && <OneButtonCookiesPopup />}
      {!enableCookiesPopup && <GTMInitializer />}
      {showDemoPopup && <DemoPopup />}
      <MultiPopupManager showMultiPopup={showDemoPopup} />
      <TerminalLoginPopupManager />
    </>
  );
};

DefaultLayout.propTypes = {
  AuthorizationLoader: PropTypes.elementType.isRequired,
  betslipNavConfig: PropTypes.shape({
    hasExtended: PropTypes.bool,
    hasNewNavigation: PropTypes.bool,
    hasSimple: PropTypes.bool,
  }).isRequired,
  children: PropTypes.node,
  isSimplifiedView: PropTypes.bool.isRequired,
  layoutClassName: PropTypes.string.isRequired,
  localBetslipState: PropTypes.object.isRequired,
  wrapperClassName: PropTypes.string.isRequired,
};

export default DefaultLayout;
